import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

interface HeaderProps {
  currentPage?: string;
  onNavigate?: (page: string) => void;
  hideTabs?: boolean;
}

const Header: React.FC<HeaderProps> = ({ currentPage = 'planner', onNavigate, hideTabs = false }) => {
  const { user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
      // Close more menu when clicking outside
      const target = event.target as HTMLElement;
      if (!target.closest('[data-testid="nav-more"]') && !target.closest('[data-testid^="nav-"]')) {
        setShowMoreMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      console.log('Logout initiated...');
      logout();
      setShowDropdown(false);
      console.log('Logout completed');
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const formatUserName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user?.username || user?.email || 'User';
  };

  const getUserInitials = () => {
    if (user?.firstName) {
      return user.firstName[0].toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  };

  return (
    <header className="header">
      <div className="container">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          height: '4rem',
          width: '100%'
        }}>
          {/* Logo and Navigation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', flex: '1' }}>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
              <div style={{ fontSize: '1.875rem', fontWeight: 700 }} className="text-gradient">
                Yojana
              </div>
              <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500 }}>
                {(user?.firstName || user?.username || 'Your')}'s AI Productivity tool
              </div>
            </div>
            
            {/* Navigation Menu - Hidden when sidebar is used */}
            {!hideTabs && (
            <nav style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => onNavigate?.('planner')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: currentPage === 'planner' ? 'var(--primary-color, #3b82f6)' : 'transparent',
                  color: currentPage === 'planner' ? 'white' : '#374151',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== 'planner') {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== 'planner') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                Planner
              </button>
              
              <button
                onClick={() => onNavigate?.('calendar')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: currentPage === 'calendar' ? 'var(--primary-color, #3b82f6)' : 'transparent',
                  color: currentPage === 'calendar' ? 'white' : '#374151',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== 'calendar') {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== 'calendar') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                Calendar
              </button>
              
              <button
                onClick={() => onNavigate?.('analytics')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: currentPage === 'analytics' ? 'var(--primary-color, #3b82f6)' : 'transparent',
                  color: currentPage === 'analytics' ? 'white' : '#374151',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== 'analytics') {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== 'analytics') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                Analytics
              </button>

              <button
                onClick={() => onNavigate?.('habits')}
                data-testid="nav-habits"
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: currentPage === 'habits' ? 'var(--primary-color, #3b82f6)' : 'transparent',
                  color: currentPage === 'habits' ? 'white' : '#374151',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== 'habits') {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== 'habits') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                Habits
              </button>

              <button
                onClick={() => onNavigate?.('reflection')}
                data-testid="nav-reflection"
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: currentPage === 'reflection' ? 'var(--primary-color, #3b82f6)' : 'transparent',
                  color: currentPage === 'reflection' ? 'white' : '#374151',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== 'reflection') {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== 'reflection') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                Reflection
              </button>

              {/* More Dropdown for additional features */}
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowMoreMenu(!showMoreMenu)}
                  data-testid="nav-more"
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: ['meals', 'cleaning', 'goals'].includes(currentPage) ? 'var(--primary-color, #3b82f6)' : 'transparent',
                    color: ['meals', 'cleaning', 'goals'].includes(currentPage) ? 'white' : '#374151',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  More
                  <span style={{ fontSize: '10px' }}>{showMoreMenu ? '▲' : '▼'}</span>
                </button>
                {showMoreMenu && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '4px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    minWidth: '160px',
                    overflow: 'hidden'
                  }}>
                    {[
                      { key: 'meals', label: 'Meal Planner' },
                      { key: 'cleaning', label: 'Cleaning' },
                      { key: 'goals', label: 'Goals' }
                    ].map(item => (
                      <button
                        key={item.key}
                        onClick={() => { onNavigate?.(item.key); setShowMoreMenu(false); }}
                        data-testid={`nav-${item.key}`}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '10px 16px',
                          border: 'none',
                          backgroundColor: currentPage === item.key ? '#eff6ff' : 'transparent',
                          color: currentPage === item.key ? '#2563eb' : '#374151',
                          textAlign: 'left',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: currentPage === item.key ? 600 : 400
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentPage === item.key ? '#eff6ff' : 'transparent'}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </nav>
            )}

            <div 
              style={{ 
                fontSize: '0.875rem', 
                color: '#64748b',
                display: window.innerWidth > 768 ? 'block' : 'none'
              }}
            >
              {new Date().toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>
          </div>

          {/* User Menu */}
          <div style={{ position: 'relative' }}>
            <button
              ref={buttonRef}
              onClick={() => setShowDropdown(!showDropdown)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                color: '#374151',
                padding: '0.5rem',
                borderRadius: '0.5rem',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease',
                fontSize: '14px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div 
                style={{ 
                  width: '2rem', 
                  height: '2rem', 
                  backgroundColor: 'var(--primary-color, #667eea)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 'bold'
                }}
              >
                {getUserInitials()}
              </div>

              <div style={{ 
                display: window.innerWidth > 640 ? 'block' : 'none',
                textAlign: 'left'
              }}>
                <div style={{ fontWeight: 500, lineHeight: '1.2' }}>
                  {formatUserName()}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af', lineHeight: '1.2' }}>
                  {user?.email}
                </div>
              </div>

              <svg 
                style={{
                  width: '1rem', 
                  height: '1rem',
                  transition: 'transform 0.15s ease',
                  transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)'
                }}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showDropdown && (
              <div 
                ref={dropdownRef}
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: '0',
                  minWidth: '200px',
                  backgroundColor: 'white',
                  borderRadius: '0.5rem',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                  border: '1px solid #e5e7eb',
                  zIndex: 1000,
                  marginTop: '0.25rem',
                  animation: 'slideIn 0.2s ease-out'
                }}
              >
                <div style={{ padding: '0.5rem 0' }}>
                  <div style={{ 
                    padding: '0.75rem 1rem', 
                    borderBottom: '1px solid #f1f5f9',
                    backgroundColor: '#f8fafc'
                  }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      {formatUserName()}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      {user?.email}
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setShowDropdown(false);
                      onNavigate?.('analytics');
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      backgroundColor: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      color: '#374151',
                      transition: 'background-color 0.15s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    📊 View Analytics
                  </button>

                  <div style={{ 
                    height: '1px', 
                    backgroundColor: '#f1f5f9', 
                    margin: '0.5rem 0' 
                  }}></div>

                  <button
                    onClick={handleLogout}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      backgroundColor: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      color: '#dc2626',
                      transition: 'background-color 0.15s ease',
                      fontWeight: 500
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#fef2f2';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    🚪 Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
